import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GitBranch, ChevronRight, Clock, Users, FileText } from 'lucide-react';

export default function ModeActivitiesTab() {
  const [selectedMode, setSelectedMode] = useState('');
  const [selectedSpecialMode, setSelectedSpecialMode] = useState('');

  const procurementModes = [
    { value: 'competitive-bidding', label: 'Competitive Bidding' },
    { value: 'limited-source', label: 'Limited Source Bidding' },
    { value: 'competitive-dialogue', label: 'Competitive Dialogue' },
    { value: 'direct-contracting', label: 'Direct Contracting' },
    { value: 'repeat-order', label: 'Repeat Order' },
    { value: 'small-value', label: 'Small Value Procurement' },
    { value: 'negotiated-two-failed', label: 'Negotiated Procurement - Two Failed Biddings' },
    { value: 'negotiated-emergency', label: 'Negotiated Procurement - Emergency Cases' },
    { value: 'negotiated-takeover', label: 'Negotiated Procurement - Take-Over Contracts' },
    { value: 'negotiated-adjacent', label: 'Negotiated Procurement - Adjacent/Contiguous' },
    { value: 'negotiated-agency', label: 'Negotiated Procurement - Agency-to-Agency' },
    { value: 'negotiated-scientific', label: 'Negotiated Procurement - Scientific/Scholarly/Artistic Work' },
    { value: 'negotiated-defense-coop', label: 'Negotiated Procurement – Defense Cooperation Agreement and Inventory-Based Items' },
    { value: 'negotiated-defense-inv', label: 'Negotiated Procurement - Defense Inventory-Based' },
    { value: 'negotiated-lease-property', label: 'Negotiated Procurement - Lease of Real Property' },
    { value: 'negotiated-lease-venue', label: 'Negotiated Procurement - Lease of Venue' },
    { value: 'negotiated-ngo', label: 'Negotiated Procurement - NGO Participation' },
    { value: 'negotiated-community', label: 'Negotiated Procurement - Community Participation' },
    { value: 'negotiated-farmers', label: 'Negotiated Procurement - Direct from Farmers/Fisherfolks' },
    { value: 'negotiated-pol', label: 'Negotiated Procurement - Direct Retail Purchase of POL, Electronic Charging Devices, and Online Subscription' },
    { value: 'unsolicited', label: 'Unsolicited Offer with Bid Matching' },
    { value: 'direct-acquisition', label: 'Direct Acquisition' },
    { value: 'direct-sales', label: 'Direct Sales' },
    { value: 'direct-sti', label: 'Direct Procurement for STI (in accordance with GPPB Resolution No. 04-2026)' }
  ];

  const specialProcurementModes = [
    { value: 'sagip-saka', label: 'Sagip Saka Procurement (GPPB Resolution No. 08-2025)' },
    { value: 'international-events', label: 'Negotiated Procurement for International Events, Conferences, Summits, or Activities Hosted by the Philippine Government (GPPB Resolution No. 01-2026)' }
  ];

  const processFlows = {
    'competitive-bidding': {
      title: 'Competitive Bidding',
      timeline: '26 to 108 calendar days (excluding higher authority approval)',
      preSteps: [
        { step: 'Strategic Procurement Planning', desc: 'Market Scoping and Project Costing', responsible: 'Procuring Entity', time: '-' },
        { step: 'Indicative APP', desc: 'End-user units formulate PPMPs', responsible: 'End-user/PE', time: '-' },
        { step: 'Early Procurement Activities (EPA)', desc: 'Conduct activities from ITB publication to BAC recommendation', responsible: 'PE', time: '-' },
        { step: 'Approved Budget for Contract (ABC)', desc: 'Ceiling amount based on approved budget', responsible: 'PE', time: '-' },
        { step: 'Approved APP', desc: 'If appropriation differs from NEP, revise APP', responsible: 'PE', time: '-' }
      ],
      steps: [
        { step: '1', title: 'Pre-Procurement Conference', desc: 'Confirm contract description, scope, ABC, duration. Ensure alignment with PPMP and APP. Determine readiness and review criteria.', responsible: 'BAC, End-user', time: 'Max 1 CD' },
        { step: '2', title: 'Posting/Publication of ITB', desc: 'Publish Invitation to Bid on PhilGEPS, PE premises, website/social media, or newspaper. Include brief description, criteria, deadline, ABC, source of funds.', responsible: 'PE', time: '7 CDs (continuously)' },
        { step: '3', title: 'Pre-Bid Conference', desc: 'Discuss, clarify, and explain eligibility requirements, terms, conditions, and specifications. Mandatory if ABC ≥ ₱1M.', responsible: 'BAC', time: 'At least 12 CDs before deadline' },
        { step: '4', title: 'Deadline for Submission', desc: 'Bidders submit their bids. Period from posting to bid opening shall not exceed 45 CDs.', responsible: 'Bidders, PE', time: 'Max 45 CDs from posting' },
        { step: '5', title: 'Bid Opening', desc: 'Open bids not later than 1 CD after deadline.', responsible: 'BAC', time: '1 CD' },
        { step: '', title: 'Preliminary Examination', desc: 'Opening of two sealed envelopes (pass/fail): 1st Envelope (Legal & Technical), 2nd Envelope (Financial).', responsible: 'BAC', time: 'Part of opening' },
        { step: '6', title: 'Bid Evaluation', desc: 'Evaluate and compare passing bids using specific criteria (LCB/MEAB for Goods & Infra, HRB for Consulting).', responsible: 'BAC', time: '7 CDs' },
        { step: '7', title: 'Post-Qualification', desc: 'Verify and validate authenticity of submitted eligibility documents and other statements.', responsible: 'BAC', time: '12 CDs (extendible)' },
        { step: '8', title: 'Approval/Notice of Award', desc: 'HoPE approves or disapproves BAC recommendation. Issue Notice of Award if approved.', responsible: 'HoPE, BAC', time: '10 CDs' },
        { step: '9', title: 'Contract Preparation & Signing', desc: 'Prepare and sign Contract Agreement and necessary documents after all documentary requirements are met.', responsible: 'PE, Winning Bidder', time: '10 CDs' },
        { step: '10', title: 'Higher Authority Approval', desc: 'Obtain approval for contracts exceeding certain thresholds (if necessary).', responsible: 'Higher Authority', time: '15 CDs (20 for GOCCs)' },
        { step: '11', title: 'Notice to Proceed', desc: 'Issue NTP to winning bidder after contract approval.', responsible: 'PE', time: 'Not later than 3 CDs' }
      ]
    },
    'limited-source': {
      title: 'Limited Source Bidding',
      timeline: 'Similar to Competitive Bidding with reduced timelines',
      preSteps: [
        { step: 'Strategic Procurement Planning', desc: 'Identify limited suppliers with known experience', responsible: 'PE', time: '-' }
      ],
      steps: [
        { step: '1', title: 'Pre-Procurement Conference', desc: 'Same as Competitive Bidding', responsible: 'BAC, End-user', time: 'Max 1 CD' },
        { step: '2', title: 'Direct Invitation to Bid', desc: 'BAC directly invites pre-selected suppliers/contractors/consultants. Posting for 3 CDs on PhilGEPS.', responsible: 'BAC', time: '3 CDs' },
        { step: '3-11', title: 'Same as Competitive Bidding', desc: 'Follow Competitive Bidding procedures with reduced timelines as applicable.', responsible: 'Various', time: 'Reduced per IRR' }
      ]
    },
    'competitive-dialogue': {
      title: 'Competitive Dialogue',
      timeline: 'Similar to Competitive Bidding with dialogue phase',
      preSteps: [
        { step: 'Pre-Procurement Activities', desc: 'Identify complex requirements requiring dialogue', responsible: 'PE', time: '-' }
      ],
      steps: [
        { step: '1', title: 'Invitation to Prospective Bidders', desc: 'Extend invitation for eligibility requirements and initial technical proposals.', responsible: 'PE', time: '7 CDs posting' },
        { step: '2', title: 'Evaluation & Selection', desc: 'BAC evaluates technical merits and selects participants for dialogue.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Dialogue Phase', desc: 'Conduct structured dialogue with eligible bidders to refine requirements.', responsible: 'BAC, Bidders', time: 'As needed' },
        { step: '4', title: 'Revised Technical Specifications', desc: 'Issue revised specifications to participants.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'Submission of Revised Bids', desc: 'Participants submit revised technical and price proposals.', responsible: 'Bidders', time: '-' },
        { step: '6-11', title: 'Bid Opening to NTP', desc: 'Follow Competitive Bidding procedures from bid opening onwards.', responsible: 'Various', time: 'As per CB' }
      ]
    },
    'direct-contracting': {
      title: 'Direct Contracting',
      timeline: 'Significantly shorter than Competitive Bidding',
      preSteps: [],
      steps: [
        { step: '1', title: 'Prepare RFQ or Pro-forma Invoice', desc: 'BAC prepares Request for Quotation or pro-forma invoice with terms and conditions.', responsible: 'BAC', time: '-' },
        { step: '2', title: 'Send RFQ to Direct Supplier', desc: 'BAC sends RFQ to the identified direct supplier (proprietary source/exclusive dealer).', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Negotiations', desc: 'Conduct simplified negotiations on contract terms.', responsible: 'BAC, Supplier', time: '-' },
        { step: '4', title: 'BAC Recommendation', desc: 'BAC recommends contract award to HoPE.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'Award of Contract', desc: 'Contract is awarded to the direct supplier.', responsible: 'HoPE', time: '-' },
        { step: '6', title: 'Notice to Proceed', desc: 'Issue NTP after award (implied).', responsible: 'PE', time: '-' }
      ]
    },
    'repeat-order': {
      title: 'Repeat Order',
      timeline: 'Very short - streamlined process',
      preSteps: [],
      steps: [
        { step: '1', title: 'Determine Need to Replenish', desc: 'End-user identifies need for additional goods previously procured through bidding.', responsible: 'End-user', time: '-' },
        { step: '2', title: 'Prepare Purchase Request', desc: 'End-user prepares PR with market price study and justification. Ensure within 6 months of original NTP, unit price ≤ original, quantity ≤ 25%.', responsible: 'End-user', time: '-' },
        { step: '3', title: 'BAC Recommendation', desc: 'BAC confirms conditions are met and recommends award to HoPE.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'Award of Contract', desc: 'Contract awarded to original supplier.', responsible: 'HoPE', time: '-' },
        { step: '5', title: 'Notice to Proceed', desc: 'Issue NTP after award (implied).', responsible: 'PE', time: '-' }
      ]
    },
    'small-value': {
      title: 'Small Value Procurement',
      timeline: 'Short - simplified process',
      preSteps: [],
      steps: [
        { step: '1', title: 'Submit Request for SVP', desc: 'End-user submits request to BAC with specifications, scope, terms, ABC (≤ ₱2M), and conditions.', responsible: 'End-user', time: '-' },
        { step: '2', title: 'Prepare & Send RFQ/RFP', desc: 'BAC prepares and sends RFQ or RFP to at least 3 qualified suppliers. Posting for 3 CDs (except ≤ ₱200K). Receipt of 1 quotation sufficient.', responsible: 'BAC', time: '3 CDs posting' },
        { step: '3', title: 'Abstract of Quotations', desc: 'After quotation submission, prepare Abstract of Quotations/Ratings.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'BAC Recommendation', desc: 'BAC recommends award to HoPE based on lowest quotation.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'Award & NTP', desc: 'HoPE enters into contract. NTP issued (implied).', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'negotiated-two-failed': {
      title: 'Negotiated Procurement - Two Failed Biddings',
      timeline: 'Variable depending on negotiations',
      preSteps: [],
      steps: [
        { step: '1', title: 'Mandatory Review', desc: 'BAC conducts mandatory review of terms, conditions, specifications, cost estimates. Revise eligibility if needed. ABC cannot increase >20%.', responsible: 'BAC', time: '-' },
        { step: '2', title: 'Invite Suppliers', desc: 'BAC invites at least 3 suppliers/contractors/consultants for negotiations. Posting for 3 CDs.', responsible: 'BAC', time: '3 CDs' },
        { step: '3', title: 'Communicate Requirements', desc: 'BAC communicates requirements, guidelines, clarifications equally to all participants.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'Conduct Negotiations', desc: 'BAC conducts negotiations with all bidders.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'Request Best Offers', desc: 'BAC requests best offers from all bidders based on final technical and financial requirements.', responsible: 'BAC', time: '-' },
        { step: '6', title: 'BAC Recommendation', desc: 'BAC recommends award to HoPE for Single/Lowest Calculated Responsive Quotation or Highest Rated Proposal.', responsible: 'BAC', time: '-' },
        { step: '7', title: 'Award & NTP', desc: 'Award of contract. NTP issued (implied).', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'negotiated-emergency': {
      title: 'Negotiated Procurement - Emergency Cases',
      timeline: 'Immediate - fastest mode',
      preSteps: [],
      steps: [
        { step: '1', title: 'Submit Emergency Request', desc: 'End-user submits request with supporting documents to BAC or HoPE. Must show imminent danger or time-critical situation.', responsible: 'End-user', time: 'Immediate' },
        { step: '2', title: 'APP Revision Recommendation', desc: 'BAC or designated authority recommends any revision of the APP.', responsible: 'BAC', time: 'Immediate' },
        { step: '3', title: 'Delegate Authority (Optional)', desc: 'HoPE may delegate authority for direct negotiations.', responsible: 'HoPE', time: 'Immediate' },
        { step: '4', title: 'Confirm Capability', desc: 'Confirmation of capability to address the emergency.', responsible: 'BAC/End-user', time: 'Immediate' },
        { step: '5', title: 'Immediate Award', desc: 'HoPE, upon BAC or End-user recommendation, shall immediately award the contract.', responsible: 'HoPE', time: 'Immediate' },
        { step: '6', title: 'Notice to Proceed', desc: 'Issue NTP immediately (implied).', responsible: 'PE', time: 'Immediate' }
      ]
    },
    'negotiated-takeover': {
      title: 'Negotiated Procurement - Take-Over Contracts',
      timeline: 'Variable - depends on negotiations',
      preSteps: [],
      steps: [
        { step: '1', title: 'Post-Qualify 2nd Bidder', desc: 'BAC post-qualifies and negotiates with the second lowest calculated/highest rated bidder from original bidding.', responsible: 'BAC', time: '-' },
        { step: '2', title: 'If Negotiation Fails', desc: 'If unsuccessful, repeat process with next lowest/highest bidder.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'If All Fail', desc: 'If all previous bidders fail, BAC may invite at least 3 new suppliers or resort to alternative method.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'Successful Post-Qualification', desc: 'Successful negotiation results in BAC recommendation for award.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'Award & NTP', desc: 'Contract awarded. NTP issued (implied).', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'negotiated-adjacent': {
      title: 'Negotiated Procurement - Adjacent/Contiguous',
      timeline: 'Short - direct negotiation',
      preSteps: [],
      steps: [
        { step: '1', title: 'Negotiate with Ongoing Contractor', desc: 'BAC negotiates with contractor/consultant of ongoing infrastructure/consulting project for adjacent/contiguous work.', responsible: 'BAC', time: '-' },
        { step: '2', title: 'Verify Conditions', desc: 'Verify: Original contract through CB/CD/LSB, related scope, within capacity, SLCC/NFCC covers combined value, same/lower unit prices, no delays.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'BAC Recommendation', desc: 'If negotiation successful, BAC recommends award to HoPE.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'Award & NTP', desc: 'Contract awarded. NTP issued (implied).', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'negotiated-agency': {
      title: 'Negotiated Procurement - Agency-to-Agency',
      timeline: 'Moderate - MOA preparation',
      preSteps: [],
      steps: [
        { step: '1', title: 'Justification', desc: 'End-user justifies to BAC that Agency-to-Agency is more efficient and economical.', responsible: 'End-user', time: '-' },
        { step: '2', title: 'Certificate from Servicing Agency', desc: 'Obtain certificate from Servicing Agency (e.g., PS-DBM, PITC) confirming compliance with conditions.', responsible: 'Servicing Agency', time: '-' },
        { step: '3', title: 'BAC Resolution', desc: 'BAC issues Resolution recommending Agency-to-Agency Agreement to HoPE.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'MOA Signing', desc: 'HoPE enters into Memorandum of Agreement with the Servicing Agency.', responsible: 'HoPE, Servicing Agency', time: '-' },
        { step: '5', title: 'Notice to Proceed', desc: 'Issue NTP after MOA (implied).', responsible: 'PE', time: '-' }
      ]
    },
    'negotiated-scientific': {
      title: 'Negotiated Procurement - Scientific, Scholarly or Artistic Work',
      timeline: 'Variable - depends on complexity',
      preSteps: [],
      steps: [
        { step: '1', title: 'Market Study', desc: 'End-user conducts market study to confirm potential sources for specialized work.', responsible: 'End-user', time: '-' },
        { step: '2', title: 'Negotiate with Capable Entities', desc: 'BAC negotiates with technically, legally, financially capable suppliers/contractors/consultants based on specifications.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Recommendation', desc: 'Successful negotiation is recommended to HoPE for award.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'Award & NTP', desc: 'Contract awarded. NTP issued (implied).', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'negotiated-defense-coop': {
      title: 'Negotiated Procurement – Defense Cooperation Agreement and Inventory-Based Items',
      timeline: 'Variable - international negotiations',
      preSteps: [],
      steps: [
        { step: '1', title: 'Negotiate with Foreign Agency', desc: 'BAC or end-user negotiates with agency/instrumentality of cooperating country.', responsible: 'BAC/End-user', time: '-' },
        { step: '2', title: 'Verify Local Unavailability', desc: 'BAC or end-user ensures capability and expertise are not available locally.', responsible: 'BAC/End-user', time: '-' },
        { step: '3', title: 'Validate AFP Modernization', desc: 'BAC or end-user validates item\'s inclusion in AFP Modernization Program.', responsible: 'BAC/End-user', time: '-' },
        { step: '4', title: 'Recommendation & Award', desc: 'BAC or End-user recommends award to HoPE. Contract awarded. NTP issued (implied).', responsible: 'BAC, HoPE, PE', time: '-' }
      ]
    },
    'negotiated-defense-inv': {
      title: 'Negotiated Procurement - Defense Inventory-Based Items',
      timeline: 'Variable',
      preSteps: [],
      steps: [
        { step: '1', title: 'Negotiate with Supplier', desc: 'BAC or end-user negotiates with supplier/manufacturer.', responsible: 'BAC/End-user', time: '-' },
        { step: '2', title: 'Verify Compatibility', desc: 'Ensure compatibility, interoperability, and sustainability of the item.', responsible: 'BAC/End-user', time: '-' },
        { step: '3', title: 'Verify Prior Performance', desc: 'Verify satisfactory implementation of prior contract for same item with DND/bureaus/agencies.', responsible: 'BAC/End-user', time: '-' },
        { step: '4', title: 'Recommendation & Award', desc: 'BAC recommends award to HoPE. Contract awarded. NTP issued (implied).', responsible: 'BAC, HoPE, PE', time: '-' }
      ]
    },
    'negotiated-lease-property': {
      title: 'Negotiated Procurement - Lease of Real Property',
      timeline: 'Moderate',
      preSteps: [],
      steps: [
        { step: '1', title: 'Invite Lessors', desc: 'BAC invites at least 3 prospective Lessors.', responsible: 'BAC', time: '-' },
        { step: '2', title: 'Open Price Quotations', desc: 'Price quotations are opened to determine Lowest Calculated Quotation.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Rate Properties', desc: 'Lessors\' properties are rated based on technical specifications and reasonableness of price.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'Award & NTP', desc: 'Contract awarded. NTP issued (implied).', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'negotiated-lease-venue': {
      title: 'Negotiated Procurement - Lease of Venue',
      timeline: 'Short',
      preSteps: [],
      steps: [
        { step: '1', title: 'Send RFQ to Venues', desc: 'BAC sends RFQ to at least 3 venues within the vicinity.', responsible: 'BAC', time: '-' },
        { step: '2', title: 'Rate Lowest Quotation', desc: 'Lowest Calculated Quotation venue is rated based on technical specifications.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Award & NTP', desc: 'Contract awarded. NTP issued (implied).', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'negotiated-ngo': {
      title: 'Negotiated Procurement - NGO Participation',
      timeline: 'Moderate',
      preSteps: [],
      steps: [
        { step: '1', title: 'Posting/Advertisement', desc: 'Post requirement. Posting for 3 CDs.', responsible: 'PE', time: '3 CDs' },
        { step: '2', title: 'Invite NGOs', desc: 'Invitation of at least 3 prospective NGOs.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Submission of Proposals', desc: 'NGOs submit complete project proposal, feasibility studies, designs, plans, budgets, charts.', responsible: 'NGOs', time: '-' },
        { step: '4', title: 'Opening & LCB Determination', desc: 'Opening and determination of Technical Requirement of Lowest Calculated Bid.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'MOA Signing', desc: 'After submission of eligibility documents, PE enters into MOA with NGO.', responsible: 'PE, NGO', time: '-' },
        { step: '6', title: 'Notice to Proceed', desc: 'Issue NTP after MOA (implied).', responsible: 'PE', time: '-' }
      ]
    },
    'negotiated-community': {
      title: 'Negotiated Procurement - Community Participation',
      timeline: 'Moderate',
      preSteps: [],
      steps: [
        { step: '1', title: 'Posting of RFQ', desc: 'End-user unit conducts Posting of Request for Quotation.', responsible: 'End-user', time: '-' },
        { step: '2', title: 'Open & Evaluate Quotations', desc: 'BAC opens and evaluates the submitted quotations.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Post-Qualification', desc: 'After determination of LCQ or SCQ, BAC conducts Post-Qualification.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'BAC Recommendation', desc: 'BAC recommends to HoPE the Approval of Resolution/Issuance of Notice of Award.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'Contract Preparation & Signing', desc: 'Prepare and sign contract with community/social group.', responsible: 'PE, Community', time: '-' },
        { step: '6', title: 'Posting of Contract & NTP', desc: 'Post Contract and Notice to Proceed (explicitly stated).', responsible: 'PE', time: '-' }
      ]
    },
    'negotiated-farmers': {
      title: 'Negotiated Procurement - Direct from Farmers/Fisherfolks',
      timeline: 'Short - direct purchase',
      preSteps: [],
      steps: [
        { step: '1', title: 'Direct Negotiation', desc: 'BAC directly negotiates with local farmers or fisherfolks.', responsible: 'BAC', time: '-' },
        { step: '2', title: 'Validate Documents', desc: 'BAC validates the documentary requirements.', responsible: 'BAC', time: '-' },
        { step: '3', title: 'Notice of Award', desc: 'HoPE shall issue the Notice of Award.', responsible: 'HoPE', time: '-' },
        { step: '4', title: 'Notice to Proceed', desc: 'PE shall issue Notice to Proceed (explicitly stated).', responsible: 'PE', time: '-' },
        { step: '5', title: 'Post Contract & NTP', desc: 'BAC Secretariat shall post the Contract and NTP.', responsible: 'BAC Secretariat', time: '-' }
      ]
    },
    'negotiated-pol': {
      title: 'Negotiated Procurement - Direct Retail Purchase of POL, Electronic Charging Devices, and Online Subscription',
      timeline: 'Very short - direct retail (ONLY for POL, Electronic Charging Devices, and Online Subscriptions)',
      preSteps: [],
      steps: [
        { step: '1', title: 'Determine Supplier', desc: 'End-user determines capable supplier/service provider (gas stations for POL, electronic charging stations, or online providers for subscriptions). Note: This mode is ONLY applicable to POL, electronic charging devices, and online subscriptions.', responsible: 'End-user', time: '-' },
        { step: '2', title: 'Direct Retail Purchase', desc: 'Direct purchase carried out per accounting principles, sound management, fiscal administration. Applicable ONLY to POL, electronic charging devices, and online subscriptions.', responsible: 'End-user/PE', time: '-' },
        { step: '3', title: 'Review Terms', desc: 'Review terms and conditions, including provision for compensation for delays.', responsible: 'PE', time: '-' },
        { step: '4', title: 'Warranty Security (Online)', desc: 'For online subscriptions, warranty security submission.', responsible: 'Supplier', time: '-' },
        { step: '5', title: 'Post Payment Confirmation', desc: 'Electronic copy of payment confirmation receipt and agreed terms posted as equivalent documents of NOA and Contract/PO.', responsible: 'PE', time: '-' }
      ]
    },
    'unsolicited': {
      title: 'Unsolicited Offer with Bid Matching',
      timeline: 'Variable - includes competitive challenge',
      preSteps: [],
      steps: [
        { step: '1', title: 'Receive Unsolicited Offer', desc: 'PE receives unsolicited offer involving new concepts or technologies.', responsible: 'PE', time: '-' },
        { step: '2', title: 'Review Offer', desc: 'PE reviews the unsolicited offer for merit and compliance with requirements.', responsible: 'PE', time: '-' },
        { step: '3', title: 'Comparative Bidding', desc: 'If acceptable, subject to comparative bids. Post ITB (7 CDs), conduct pre-bid conference, submit bids, open and evaluate.', responsible: 'PE, BAC', time: '7 CDs + bidding period' },
        { step: '4', title: 'Post-Qualify LCCB', desc: 'Post-qualify bidder with Lowest Calculated Comparative Bid.', responsible: 'BAC', time: '-' },
        { step: '5', title: 'Bid Matching Opportunity', desc: 'If LCCB post-qualified, original offeror given opportunity to match or submit lower offer.', responsible: 'BAC, Original Offeror', time: '-' },
        { step: '6a', title: 'If Original Offeror Matches', desc: 'Contract awarded to original offeror. Issue NOA, execute contract, seek higher approval if needed. Issue NTP.', responsible: 'HoPE, PE', time: '-' },
        { step: '6b', title: 'If Original Offeror Cannot Match', desc: 'Contract awarded to bidder that submitted LCCB. Issue NOA, execute contract. Issue NTP.', responsible: 'HoPE, PE', time: '-' }
      ]
    },
    'direct-acquisition': {
      title: 'Direct Acquisition',
      timeline: 'Very short - immediate',
      preSteps: [],
      steps: [
        { step: '1', title: 'Determine Eligibility', desc: 'PE determines non-CSE requirement falls within ₱200,000 threshold.', responsible: 'PE', time: '-' },
        { step: '2', title: 'Identify Supplier', desc: 'PE identifies supplier with required legal, technical, financial eligibility (may be through market scoping). Supplier must be PhilGEPS registered.', responsible: 'PE', time: '-' },
        { step: '3', title: 'Direct Purchase', desc: 'PE directly purchases non-CSE goods from identified supplier. Goods available on e-commerce or off-the-shelf.', responsible: 'PE', time: '-' },
        { step: '4', title: 'Purchase Order', desc: 'During purchase, PE provides copy of relevant Purchase Order to supplier, which supplier signs/conforms to for audit purposes.', responsible: 'PE, Supplier', time: '-' }
      ]
    },
    'direct-sales': {
      title: 'Direct Sales',
      timeline: 'Short',
      preSteps: [],
      steps: [
        { step: '1', title: 'Identify Non-CSE Requirements', desc: 'PE identifies its non-CSE requirements.', responsible: 'PE', time: '-' },
        { step: '2', title: 'Check PhilGEPS', desc: 'PE ascertains in PhilGEPS if similar procurement contracts have been awarded through competitive bidding.', responsible: 'PE', time: '-' },
        { step: '3', title: 'Verify Conditions', desc: 'Verify: Delivered, inspected, accepted, Certificate of Satisfactory Performance, initiated within 6 months, same/lower quantity/price.', responsible: 'PE', time: '-' },
        { step: '4', title: 'Obtain Documents', desc: 'PE obtains relevant procurement documents from original government agency.', responsible: 'PE', time: '-' },
        { step: '5', title: 'Verify Supplier Capability', desc: 'PE determines if supplier remains legally, technically, financially capable.', responsible: 'PE', time: '-' },
        { step: '6', title: 'Direct Negotiation', desc: 'PE directly negotiates with supplier (contract within original amount, same/lower unit price).', responsible: 'PE', time: '-' },
        { step: '7', title: 'Contract Signing & NTP', desc: 'If negotiations successful, parties enter into contract. NTP issued (implied).', responsible: 'PE, Supplier', time: '-' }
      ]
    },
    'direct-sti': {
      title: 'Direct Procurement for Science, Technology and Innovation (GPPB Resolution No. 04-2026)',
      timeline: 'Faster than Competitive Bidding',
      preSteps: [],
      steps: [
        { step: '1', title: 'Identify STI Requirement & Entity', desc: 'PE confirms requirement is covered by Direct Procurement for STI and profiles capable manufacturer/supplier or startup; justification and ABC prepared.', responsible: 'PE / End-User / TWG', time: 'PE-set (before BAC action, no fixed CD)' },
        { step: '2', title: 'Confirm Use of Direct Procurement for STI', desc: 'BAC reviews justification and identified entity, then issues resolution approving use of Direct Procurement for STI and naming the supplier/startup.', responsible: 'BAC', time: 'As soon as practicable (no specific CD; follow BAC\'s internal rules)' },
        { step: '3', title: 'Prepare and Issue RFQ / Proposal Request', desc: 'BAC/BAC Secretariat sends RFQ or proposal request to identified entity with STI specs, delivery, IP terms, and required eligibility documents.', responsible: 'BAC / BAC Secretariat', time: 'Immediately after Step 2 (PE-set CD)' },
        { step: '4', title: 'Evaluate Quotation & Eligibility', desc: 'BAC with End-User/TWG evaluates eligibility documents and quotation to confirm capability, STI coverage, and most advantageous price.', responsible: 'BAC (with End-User/TWG)', time: 'Upon receipt of documents, within PE-set CD' },
        { step: '5', title: 'Simplified Negotiation', desc: 'BAC or delegated unit negotiates final technical, delivery, IP, and financial terms with the identified entity to secure the most advantageous contract.', responsible: 'BAC / Delegated Unit', time: 'Within 3 CD from receipt of quotation and complete eligibility requirements' },
        { step: '6', title: 'Award, Contract/PO, and NTP', desc: 'HoPE issues NOA, signs contract/PO upon posting of required securities, and issues NTP if applicable.', responsible: 'HoPE / Authorized Official; Supplier; BAC Secretariat', time: 'NOA within 10 CD from BAC recommendation; Contract/PO within 7 CD from NOA; NTP within 3 CD from contract/PO' },
        { step: '7', title: 'Posting, Delivery, and Acceptance', desc: 'BAC Secretariat posts PO/Contract and NTP; End-User/Inspection Committee oversees delivery, contract implementation actions, and acceptance.', responsible: 'BAC Secretariat; End-User / Inspection Committee', time: 'Posting within 5 CD from PO/Contract and NTP; implementation per contract schedule' }
      ]
    }
  };

  const specialProcessFlows = {
    'sagip-saka': {
      title: 'Sagip Saka Procurement (GPPB Resolution No. 08-2025)',
      subtitle: 'Negotiated Procurement under RA 11321 and Section 35 of RA 12009 IRR',
      timeline: 'Approximately 17+ calendar days (excluding contract approval)',
      preSteps: [
        { step: 'Strategic Procurement Planning', desc: 'PE identifies agricultural and fishery products necessary for agency operations. Conduct market scoping to determine market capability.', responsible: 'PE', time: '-' },
        { step: 'Determine Terms and ABC', desc: 'Formulate project requirements. Ensure purchase price is higher than prevailing farmgate price and reasonable. Include hauling/delivery fee in ABC.', responsible: 'PE', time: '-' },
        { step: 'PPMP & APP', desc: 'Indicate adoption of Negotiated Procurement-Sagip Saka (NP-SS) in PPMP. Include in APP upon budget validation.', responsible: 'PE, HoPE', time: '-' }
      ],
      steps: [
        { step: '1', title: 'Posting of Procurement Opportunities', desc: 'BAC posts all NP-SS opportunities in FFEDIS (Farmers and Fisherfolk Enterprise Development Information System), PE\'s official website, and conspicuous place within PE premises. Optional posting on PhilGEPS or social media.', responsible: 'BAC/Secretariat', time: 'Minimum 3 CDs' },
        { step: '2', title: 'Receipt of Proposals', desc: 'FFCEs (Farmers and Fisherfolk Cooperatives and Enterprises) registered in FFEDIS submit written proposals with FFEDIS Certificate and Omnibus Sworn Statement.', responsible: 'FFCEs', time: 'During posting period' },
        { step: '3', title: 'Evaluation of Eligibility', desc: 'BAC evaluates eligibility and responsiveness. Verify FFEDIS registration and capacity to deliver according to specifications. One (1) proposal is sufficient to proceed. Disqualify if products obtained through government assistance unless from buy-back program.', responsible: 'BAC', time: '7 CDs' },
        { step: '4', title: 'Determination of Best Offer', desc: 'BAC determines best offer based on quality and price, ensuring most advantageous terms for government. Verify price is higher than farmgate price and consistent with price monitoring reports.', responsible: 'BAC', time: 'Within evaluation period' },
        { step: '5', title: 'BAC Recommendation', desc: 'BAC recommends award to the eligible FFCE with the best offer.', responsible: 'BAC', time: 'After evaluation' },
        { step: '6', title: 'Notice of Award', desc: 'HoPE approves BAC recommendation and issues Notice of Award (NOA) to winning FFCE.', responsible: 'HoPE', time: '-' },
        { step: '7', title: 'Contract Signing', desc: 'FFCE enters into contract with PE within ten (10) calendar days of receiving NOA. All documentary requirements must be complete.', responsible: 'PE, FFCE', time: '10 CDs' },
        { step: '8', title: 'Notice to Proceed', desc: 'PE issues NTP with copy of approved contract within three (3) calendar days of contract approval.', responsible: 'PE', time: '3 CDs' },
        { step: '9', title: 'Posting of Award Information', desc: 'BAC posts NOA, contract, and NTP in FFEDIS, PE\'s official website, and conspicuous place. Optional posting on PhilGEPS.', responsible: 'BAC', time: 'After NTP' },
        { step: '10', title: 'Payment & Acknowledgement', desc: 'Payments made consistent with DBM Circular Letter No. 2018-14. FFCEs acknowledge receipt by signing disbursement voucher (serves as delivery invoice). Registered FFCEs that can issue invoice must provide it.', responsible: 'PE, FFCE', time: 'Upon delivery' }
      ],
      note: 'This special procurement mode is authorized under Section 35 of RA 12009 IRR, where GPPB has rule-making authority to introduce new types of negotiated procurement based on specific sectoral needs.'
    },
    'international-events': {
      title: 'Negotiated Procurement for International Events, Conferences, Summits, or Activities Hosted by the Philippine Government (GPPB Resolution No. 01-2026)',
      subtitle: 'Special Negotiated Procurement under Section 35 of RA 12009',
      timeline: 'Flexible - based on event-specific deadlines and requirements',
      preSteps: [
        { step: 'Event Identification', desc: 'PE identifies international event formally hosted by the Philippine Government (e.g., ASEAN, APEC, SEA Games, UN meetings, treaty negotiations). Confirm impracticability of existing procurement modes due to security, protocol, or operational requirements.', responsible: 'PE', time: '-' },
        { step: 'Determine Requirements & ABC', desc: 'Formulate specific requirements including lease of hotels/venues, temporary offices for dignitaries, and critical event-related goods/services (AV systems, event management, media relations, transport). Establish ABC based on market assessment.', responsible: 'PE', time: '-' },
        { step: 'Include in APP', desc: 'Include procurement in the Annual Procurement Plan with clear justification for the negotiated procurement mode.', responsible: 'PE, HoPE', time: '-' }
      ],
      steps: [
        { step: '1', title: 'Prepare RFQ / Pro-forma Invoice', desc: 'PE prepares RFQ or pro-forma invoice with terms and conditions based on approved APP and specific event requirements. Identified supplier/venue is selected due to distinct capability to meet security, protocol, and logistical needs.', responsible: 'PE', time: '-' },
        { step: '2', title: 'Send RFQ and Negotiate', desc: 'PE sends RFQ/pro-forma invoice to the identified supplier. Conduct simplified negotiations on technical, legal, and financial terms to confirm capability and align with event requirements.', responsible: 'PE, BAC', time: '-' },
        { step: '3', title: 'Evaluate Offer and Ensure Value-for-Money', desc: 'PE ensures goods/services are obtained at the most advantageous price and contract terms. Consider safety, security, and diplomatic/protocol obligations. Basis of evaluation is not purely lowest price but best combination of compliance with international obligations, security standards, and reasonable cost.', responsible: 'BAC', time: '-' },
        { step: '4', title: 'Optional Transparency Measures', desc: 'At PE\'s option: Post the RFQ and/or contract; Require performance security; Impose retention fee (subject to existing rules). Successfully completed contracts are included in the Procurement Monitoring Report.', responsible: 'PE', time: 'Optional' },
        { step: '5', title: 'Award and Contract Approval', desc: 'Once terms are agreed upon and value-for-money is demonstrated, contract is awarded and approved by the Head of the Procuring Entity (HoPE) or duly authorized representative.', responsible: 'HoPE', time: '-' },
        { step: '6', title: 'Notice to Proceed', desc: 'PE issues NTP after contract approval (implied). Implementation begins per contract terms.', responsible: 'PE', time: '-' }
      ],
      note: 'This special procurement mode is for international events with heightened security, strict diplomatic protocol, and high-profile delegations where standard competitive bidding would be impracticable. PE must strictly document necessity, scope, and value-for-money. PE remains bound by all legal remedies under RA 12009, including blacklisting and liquidated damages.',
      coveredItems: [
        'Lease of hotels, venues, or office spaces',
        'Temporary offices for dignitaries and delegates',
        'AV and lighting systems',
        'Event management services',
        'Hosting and media relations',
        'Transport and similar critical event-related needs'
      ]
    }
  };

  const selectedFlow = processFlows[selectedMode];
  const selectedSpecialFlow = specialProcessFlows[selectedSpecialMode];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-emerald-600" />
          End-to-End Process Flow
        </CardTitle>
        <CardDescription>
          Complete process from Approved Budget for Contract (ABC) to Notice to Proceed (NTP)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Mode Selector */}
          <div>
            <Label htmlFor="mode" className="mb-2 block font-semibold">Select Procurement Mode</Label>
            <Select value={selectedMode} onValueChange={(value) => {
              setSelectedMode(value);
              setSelectedSpecialMode(''); // Clear special mode when regular mode is selected
            }}>
              <SelectTrigger id="mode" className="w-full">
                <SelectValue placeholder="Choose a procurement mode..." />
              </SelectTrigger>
              <SelectContent>
                {procurementModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Special Procurement Mode Selector */}
          <div>
            <Label htmlFor="specialMode" className="mb-2 block font-semibold">Select Special Procurement Mode</Label>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
              Special procurement modes introduced by GPPB under Section 35 of RA 12009 IRR (Rule-making authority for new types of negotiated procurement)
            </p>
            <Select value={selectedSpecialMode} onValueChange={(value) => {
              setSelectedSpecialMode(value);
              setSelectedMode(''); // Clear regular mode when special mode is selected
            }}>
              <SelectTrigger id="specialMode" className="w-full">
                <SelectValue placeholder="Choose a special procurement mode..." />
              </SelectTrigger>
              <SelectContent>
                {specialProcurementModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Process Flow Display */}
          {selectedFlow && (
            <div className="space-y-6">
              {/* Header */}
              <div className="border-l-4 border-emerald-500 pl-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-r">
                <h3 className="text-xl font-bold text-emerald-800 dark:text-emerald-200">{selectedFlow.title}</h3>
                <div className="flex items-center gap-2 mt-1 text-sm text-slate-600 dark:text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span>Timeline: {selectedFlow.timeline}</span>
                </div>
              </div>

              {/* Pre-Procurement Steps */}
              {selectedFlow.preSteps && selectedFlow.preSteps.length > 0 && (
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Pre-Procurement Activities
                  </h4>
                  <div className="space-y-3">
                    {selectedFlow.preSteps.map((item, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold">{item.step}</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{item.desc}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                <span>{item.responsible}</span>
                              </div>
                              {item.time !== '-' && (
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{item.time}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Main Process Steps */}
              <div>
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4" />
                  Process Steps (ABC to NTP)
                </h4>
                <div className="relative">
                  {/* Vertical Line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-emerald-200 dark:bg-emerald-800" />
                  
                  <div className="space-y-4">
                    {selectedFlow.steps.map((item, index) => (
                      <div key={index} className="relative">
                        {/* Step Number Circle */}
                        <div className="absolute left-0 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold z-10 shadow-md">
                          {item.step || '•'}
                        </div>
                        
                        {/* Step Content */}
                        <div className="ml-14 border rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow">
                          <h5 className="font-semibold text-base mb-2">{item.title}</h5>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{item.desc}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs">
                            <div className="flex items-center gap-1 text-slate-500">
                              <Users className="w-3 h-3" />
                              <span className="font-medium">Responsible:</span>
                              <span>{item.responsible}</span>
                            </div>
                            {item.time && item.time !== '-' && (
                              <div className="flex items-center gap-1 text-emerald-600">
                                <Clock className="w-3 h-3" />
                                <span className="font-medium">{item.time}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer Note */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r text-sm">
                <p>
                  <strong>Note:</strong> Timeline may vary based on specific project complexity, procurement entity policies, and compliance requirements. Calendar Days (CDs) are used throughout. Always refer to RA 12009 and its IRR for official guidance.
                </p>
              </div>
            </div>
          )}

          {/* Special Process Flow Display */}
          {selectedSpecialFlow && (
            <div className="space-y-6">
              {/* Header */}
              <div className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-r">
                <h3 className="text-xl font-bold text-blue-800 dark:text-blue-200">{selectedSpecialFlow.title}</h3>
                {selectedSpecialFlow.subtitle && (
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">{selectedSpecialFlow.subtitle}</p>
                )}
                <div className="flex items-center gap-2 mt-1 text-sm text-slate-600 dark:text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span>Timeline: {selectedSpecialFlow.timeline}</span>
                </div>
              </div>

              {/* Pre-Procurement Steps */}
              {selectedSpecialFlow.preSteps && selectedSpecialFlow.preSteps.length > 0 && (
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Pre-Procurement Activities
                  </h4>
                  <div className="space-y-3">
                    {selectedSpecialFlow.preSteps.map((item, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold">{item.step}</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{item.desc}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                <span>{item.responsible}</span>
                              </div>
                              {item.time !== '-' && (
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{item.time}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Main Process Steps */}
              <div>
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4" />
                  Process Steps (ABC to NTP)
                </h4>
                <div className="relative">
                  {/* Vertical Line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-800" />
                  
                  <div className="space-y-4">
                    {selectedSpecialFlow.steps.map((item, index) => (
                      <div key={index} className="relative">
                        {/* Step Number Circle */}
                        <div className="absolute left-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold z-10 shadow-md">
                          {item.step || '•'}
                        </div>
                        
                        {/* Step Content */}
                        <div className="ml-14 border rounded-lg p-4 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow">
                          <h5 className="font-semibold text-base mb-2">{item.title}</h5>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{item.desc}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs">
                            <div className="flex items-center gap-1 text-slate-500">
                              <Users className="w-3 h-3" />
                              <span className="font-medium">Responsible:</span>
                              <span>{item.responsible}</span>
                            </div>
                            {item.time && item.time !== '-' && (
                              <div className="flex items-center gap-1 text-blue-600">
                                <Clock className="w-3 h-3" />
                                <span className="font-medium">{item.time}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Special Note */}
              {selectedSpecialFlow.note && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r text-sm">
                  <p>
                    <strong>Special Note:</strong> {selectedSpecialFlow.note}
                  </p>
                </div>
              )}

              {/* Covered Items */}
              {selectedSpecialFlow.coveredItems && selectedSpecialFlow.coveredItems.length > 0 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 rounded-r">
                  <p className="font-semibold text-purple-800 dark:text-purple-200 mb-2">Covered Items:</p>
                  <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-1">
                    {selectedSpecialFlow.coveredItems.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-purple-500 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Footer Note */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r text-sm">
                <p>
                  <strong>Note:</strong> Timeline may vary based on specific project complexity, procurement entity policies, and compliance requirements. Calendar Days (CDs) are used throughout. Always refer to RA 12009 and its IRR for official guidance.
                </p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!selectedMode && !selectedSpecialMode && (
            <div className="text-center py-12 text-slate-500">
              <GitBranch className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Select a procurement mode or special procurement mode above to view its complete process flow</p>
              <p className="text-sm mt-2">From ABC to Notice to Proceed</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
